"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X, ChevronDown } from "lucide-react";
import { EXTERNAL_LINKS } from "@/lib/constants";

const productLinks = [
  {
    name: "Shopping Assistant",
    description: "Search, recommend, and add to cart in conversation",
    href: "/shopping-assistant",
  },
  {
    name: "Semantic Search",
    description: "Vector + keyword search with reranking",
    href: "/semantic-search",
  },
  {
    name: "Product Intelligence",
    description: "Essences that understand why people buy",
    href: "/product-intelligence",
  },
  {
    name: "Assistant Management",
    description: "Monitor conversations, track revenue impact",
    href: "/assistant-management",
  },
  {
    name: "Assistant Extensions",
    description: "Add order history, profiles, delivery tracking",
    href: "/assistant-extensions",
  },
];

const resourceLinks = [
  {
    name: "About Us",
    description: "Our story & team",
    href: "/about",
  },
  {
    name: "Security",
    description: "Security practices, GDPR, data handling",
    href: "/security",
  },
  {
    name: "Contact",
    description: "Get in touch",
    href: "/contact",
  },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
          isScrolled
            ? "bg-bg/95 border-b border-[var(--border-subtle)]"
            : "bg-transparent"
        }`}
      >
        <div className="container">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 group">
              <Image
                src="/images/logo.svg"
                alt="IsarTech Logo"
                width={48}
                height={48}
                className="h-8 w-8 sm:h-10 sm:w-10 transition-transform duration-300 group-hover:scale-105"
              />
              <span className="text-xl font-bold text-fg">IsarTech</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {/* Product Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setActiveDropdown("product")}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1.5 px-4 py-2.5 text-[15px] font-medium text-fg-muted hover:text-fg transition-colors rounded-lg hover:bg-[var(--white-3)]">
                  Product
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      activeDropdown === "product" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                    activeDropdown === "product"
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 -translate-y-2 pointer-events-none"
                  }`}
                >
                  <div className="w-72 p-2 bg-bg-elevated border border-[var(--border-subtle)] rounded-2xl shadow-2xl">
                    {productLinks.map((link) => (
                      <Link
                        key={link.name}
                        href={link.href}
                        className="flex flex-col gap-0.5 p-3 rounded-xl hover:bg-[var(--white-3)] transition-colors group"
                      >
                        <span className="text-[15px] font-medium text-fg group-hover:text-accent transition-colors">
                          {link.name}
                        </span>
                        <span className="text-[13px] text-fg-subtle">
                          {link.description}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Integrations Link */}
              <Link
                href="/integrations"
                className="px-4 py-2.5 text-[15px] font-medium text-fg-muted hover:text-fg transition-colors rounded-lg hover:bg-[var(--white-3)]"
              >
                Integrations
              </Link>

              {/* Pricing Link */}
              <Link
                href="/pricing"
                className="px-4 py-2.5 text-[15px] font-medium text-fg-muted hover:text-fg transition-colors rounded-lg hover:bg-[var(--white-3)]"
              >
                Pricing
              </Link>

              {/* Resources Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setActiveDropdown("resources")}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="flex items-center gap-1.5 px-4 py-2.5 text-[15px] font-medium text-fg-muted hover:text-fg transition-colors rounded-lg hover:bg-[var(--white-3)]">
                  Resources
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      activeDropdown === "resources" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                    activeDropdown === "resources"
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 -translate-y-2 pointer-events-none"
                  }`}
                >
                  <div className="w-72 p-2 bg-bg-elevated border border-[var(--border-subtle)] rounded-2xl shadow-2xl">
                    {resourceLinks.map((link) => (
                      <Link
                        key={link.name}
                        href={link.href}
                        className="flex flex-col gap-0.5 p-3 rounded-xl hover:bg-[var(--white-3)] transition-colors group"
                      >
                        <span className="text-[15px] font-medium text-fg group-hover:text-accent transition-colors">
                          {link.name}
                        </span>
                        <span className="text-[13px] text-fg-subtle">
                          {link.description}
                        </span>
                      </Link>
                    ))}
                    <div className="border-t border-[var(--border-subtle)] mt-2 pt-2">
                      <a
                        href={EXTERNAL_LINKS.docs}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded-xl hover:bg-[var(--white-3)] transition-colors group"
                      >
                        <span className="text-[15px] font-medium text-fg group-hover:text-accent transition-colors">
                          Documentation
                        </span>
                        <svg
                          className="w-4 h-4 text-fg-subtle group-hover:text-accent transition-colors"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                href={EXTERNAL_LINKS.authLogin}
                className="btn-ghost"
              >
                Sign In
              </Link>
              <Link href="/contact" className="btn-primary">
                Book Demo
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-3 text-fg hover:bg-[var(--white-3)] rounded-lg transition-colors"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 w-full max-w-sm h-full bg-bg border-l border-[var(--border-subtle)] transition-transform duration-300 ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full pt-24 pb-6 px-6">
            <nav className="flex-1 overflow-y-auto space-y-6">
              {/* Product Section */}
              <div>
                <h3 className="text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                  Product
                </h3>
                <div className="space-y-1">
                  {productLinks.map((link) => (
                    <Link
                      key={link.name}
                      href={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3.5 text-[15px] text-fg-muted hover:text-fg transition-colors"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div>
                <h3 className="text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                  Services
                </h3>
                <div className="space-y-1">
                  <Link
                    href="/integrations"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-3.5 text-[15px] text-fg-muted hover:text-fg transition-colors"
                  >
                    Integrations
                  </Link>
                  <Link
                    href="/custom-integration"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-3.5 text-[15px] text-fg-muted hover:text-fg transition-colors"
                  >
                    Custom Integration
                  </Link>
                  <Link
                    href="/pricing"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-3.5 text-[15px] text-fg-muted hover:text-fg transition-colors"
                  >
                    Pricing
                  </Link>
                </div>
              </div>

              {/* Resources Section */}
              <div>
                <h3 className="text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                  Resources
                </h3>
                <div className="space-y-1">
                  {resourceLinks.map((link) => (
                    <Link
                      key={link.name}
                      href={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3.5 text-[15px] text-fg-muted hover:text-fg transition-colors"
                    >
                      {link.name}
                    </Link>
                  ))}
                  <a
                    href={EXTERNAL_LINKS.docs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block py-2 text-[15px] text-fg-muted hover:text-fg transition-colors"
                  >
                    Documentation
                  </a>
                </div>
              </div>
            </nav>

            {/* Mobile CTA */}
            <div className="space-y-3 pt-6 border-t border-[var(--border-subtle)]">
              <Link
                href={EXTERNAL_LINKS.authLogin}
                onClick={() => setIsMenuOpen(false)}
                className="btn-secondary w-full"
              >
                Sign In
              </Link>
              <Link
                href="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="btn-primary w-full"
              >
                Book Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
